import { type MealServiceListItem, type MealServiceListQuery } from '@utazzo/shared';
import { UzBadge, UzButton, UzStatusBadge } from '@utazzo/ui';
import { FileSpreadsheet } from 'lucide-react';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { listMealServices } from '../meal-services.api';
import {
  useBulkDeleteMealServices,
  useDeleteMealService,
  useMealServiceFacets,
  useMealServicesList,
} from '../use-meal-services';

import { type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { ListScreen, type ListScreenColumns } from '@/components/list-screen';
import { useCurrentUser } from '@/features/auth/use-auth';
import {
  useCatalogGeoFilters,
  useSelectedCountryId,
  withGeoFilters,
} from '@/hooks/use-catalog-geo-filters';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PENDING_APPROVAL', label: 'Pending approval' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'ARCHIVED', label: 'Archived' },
] as const;

const MEAL_KIND_OPTIONS = [
  { value: '', label: 'All meal kinds' },
  { value: 'BREAKFAST', label: 'Breakfast' },
  { value: 'BRUNCH', label: 'Brunch' },
  { value: 'LUNCH', label: 'Lunch' },
  { value: 'HIGH_TEA', label: 'High tea' },
  { value: 'DINNER', label: 'Dinner' },
  { value: 'SNACKS', label: 'Snacks' },
] as const;

const SEATING_OPTIONS = [
  { value: '', label: 'All seating' },
  { value: 'SHARED', label: 'Shared' },
  { value: 'PRIVATE', label: 'Private' },
  { value: 'EXCLUSIVE', label: 'Exclusive' },
] as const;

const APPROVAL_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'approved', label: 'Approved only' },
  { value: 'pending', label: 'Not approved' },
] as const;

const ARCHIVED_OPTIONS = [
  { value: '', label: 'Active only' },
  { value: 'archived', label: 'Include archived' },
] as const;

const MIN_SEARCH_LENGTH = 3;

const FILTERS: ReadonlyArray<DataGridFilter> = [
  {
    id: 'mealKind',
    label: 'Meal kind',
    options: MEAL_KIND_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-48',
  },
  {
    id: 'seatingType',
    label: 'Seating',
    options: SEATING_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-44',
  },
  {
    id: 'status',
    label: 'Status',
    options: STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-44',
  },
  {
    id: 'approval',
    label: 'Approval',
    options: APPROVAL_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-40',
  },
  {
    id: 'archived',
    label: 'Archived',
    options: ARCHIVED_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-44',
  },
];

const MEAL_KIND_LABEL: Record<MealServiceListItem['mealKind'], string> = {
  BREAKFAST: 'Breakfast',
  BRUNCH: 'Brunch',
  LUNCH: 'Lunch',
  HIGH_TEA: 'High tea',
  DINNER: 'Dinner',
  SNACKS: 'Snacks',
};

const formatMoney = (amount: number, currencyHint = ''): string => {
  if (!Number.isFinite(amount)) return String(amount);
  return `${currencyHint}${amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

const mapStatus = (
  s: MealServiceListItem['status'],
): 'active' | 'inactive' | 'draft' | 'pending' => {
  switch (s) {
    case 'ACTIVE':
      return 'active';
    case 'DRAFT':
      return 'draft';
    case 'PENDING_APPROVAL':
      return 'pending';
    default:
      return 'inactive';
  }
};

export const MealServicesListPage = (): React.JSX.Element => {
  const me = useCurrentUser();
  const canDelete = me?.role === 'ADMIN';
  const deleteMealService = useDeleteMealService();
  const bulkDeleteMut = useBulkDeleteMealServices();
  const navigate = useNavigate();

  // Country / City, from the meal-service facets (in-use places only). City
  // narrows to the selected Country; switching Country clears a stale City -
  // see `useDataGridState.setFilter`.
  const facets = useMealServiceFacets();
  const { countryFilter, cityFilter } = useCatalogGeoFilters(facets.data, useSelectedCountryId());
  const filters = useMemo(
    () => withGeoFilters(FILTERS, countryFilter, cityFilter, 'seatingType'),
    [countryFilter, cityFilter],
  );

  const columns: ListScreenColumns<MealServiceListItem> = ({ state }) => {
    const cols: ReadonlyArray<DataGridColumn<MealServiceListItem>> = [
      {
        id: 'serial',
        header: 'S.No',
        width: '64px',
        cell: (_row, index) => (
          <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
        ),
      },
      {
        id: 'name',
        header: 'Meal service',
        sortable: true,
        cell: (row) => (
          <div className="flex flex-col">
            <span className="font-medium text-zinc-900">{row.name}</span>
            {row.restaurantName && (
              <span className="text-xs text-zinc-500">{row.restaurantName}</span>
            )}
          </div>
        ),
      },
      {
        id: 'mealKind',
        header: 'Meal kind',
        sortable: true,
        width: '130px',
        cell: (row) => <UzBadge tone="secondary">{MEAL_KIND_LABEL[row.mealKind]}</UzBadge>,
      },
      {
        id: 'cuisine',
        header: 'Cuisine',
        cell: (row) => (
          <span className="text-sm text-zinc-900">
            {row.cuisineName ?? <span className="text-zinc-400">-</span>}
          </span>
        ),
      },
      {
        id: 'location',
        header: 'Location',
        cell: (row) => (
          <div className="flex flex-col text-sm">
            <span className="text-zinc-900">{row.cityName}</span>
            <span className="text-xs text-zinc-500">{row.countryName}</span>
          </div>
        ),
      },
      {
        id: 'seating',
        header: 'Seating',
        width: '110px',
        cell: (row) => (
          <span className="text-xs tracking-wide text-zinc-600 uppercase">{row.seatingType}</span>
        ),
      },
      {
        id: 'vendor',
        header: 'Vendor',
        cell: (row) => (
          <span className="text-sm text-zinc-900">
            {row.vendorSupplierName ?? <span className="text-zinc-400">-</span>}
          </span>
        ),
      },
      {
        id: 'adultCost',
        header: 'Adult cost',
        width: '120px',
        cell: (row) => (
          <span className="font-mono text-sm text-zinc-700">
            {formatMoney(row.costPerAdult, row.currencyCode ? `${row.currencyCode} ` : '')}
          </span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        width: '140px',
        cell: (row) => (
          <div className="flex flex-col gap-1">
            <UzStatusBadge status={mapStatus(row.status)} />
            {row.isApprovedByAdmin && (
              <span className="text-[10px] tracking-wide text-emerald-600 uppercase">
                Admin approved
              </span>
            )}
          </div>
        ),
      },
    ];
    return cols;
  };

  return (
    <ListScreen<MealServiceListItem, MealServiceListQuery>
      title="Meal services"
      description="Restaurant / dining experiences bookable on an itinerary day."
      useList={useMealServicesList}
      defaultSort={{ by: 'createdAt', dir: 'desc' }}
      minSearchLength={MIN_SEARCH_LENGTH}
      toQuery={(state, base) => {
        const status = state.filters.status;
        const mealKind = state.filters.mealKind;
        const seatingType = state.filters.seatingType;
        const approval = state.filters.approval;
        const archived = state.filters.archived;
        const countryId = state.filters.countryId;
        const cityId = state.filters.cityId;
        return {
          ...base,
          ...(status ? { status: status as MealServiceListQuery['status'] } : {}),
          ...(countryId ? { countryId } : {}),
          ...(cityId ? { cityId } : {}),
          ...(mealKind ? { mealKind: mealKind as MealServiceListQuery['mealKind'] } : {}),
          ...(seatingType
            ? { seatingType: seatingType as MealServiceListQuery['seatingType'] }
            : {}),
          ...(approval === 'approved' ? { isApprovedByAdmin: true } : {}),
          ...(approval === 'pending' ? { isApprovedByAdmin: false } : {}),
          ...(archived === 'archived' ? { includeDeleted: true } : {}),
        };
      }}
      columns={columns}
      filters={filters}
      getRowId={(row) => row.id}
      createButton={{ label: 'Add meal service', onClick: () => navigate('/meal-services/new') }}
      headerExtra={
        <UzButton variant="outline" size="sm" onClick={() => navigate('/meal-services/imports')}>
          <FileSpreadsheet className="mr-1.5 h-4 w-4" />
          Bulk import
        </UzButton>
      }
      onRowClick={(row) => navigate(`/meal-services/${row.id}/edit`)}
      rowActions={{
        edit: false,
        delete: {
          onDelete: (row) => deleteMealService.mutateAsync(row.id),
          disabled: () => !canDelete,
          confirm: (row) => ({
            title: 'Delete this meal service?',
            description: `${row.name} will be soft-deleted and hidden from lists. Itinerary components using it are unaffected.`,
          }),
        },
      }}
      bulkActions={{
        delete: {
          onDelete: async (rows) => {
            await bulkDeleteMut.mutateAsync(rows.map((r) => r.id));
          },
          confirm: (rows) => ({
            title: `Delete ${rows.length} meal service${rows.length === 1 ? '' : 's'}?`,
            description:
              'These meal services will be soft-deleted. Itinerary components using them are unaffected.',
          }),
        },
      }}
      export={{
        fetchPage: (q) => listMealServices(q),
        filename: 'meal-services',
        csv: [
          { header: 'Name', value: (r) => r.name },
          { header: 'Restaurant', value: (r) => r.restaurantName ?? '' },
          { header: 'Meal kind', value: (r) => MEAL_KIND_LABEL[r.mealKind] },
          { header: 'Cuisine', value: (r) => r.cuisineName ?? '' },
          { header: 'City', value: (r) => r.cityName },
          { header: 'Country', value: (r) => r.countryName },
          { header: 'Seating', value: (r) => r.seatingType },
          { header: 'Vendor', value: (r) => r.vendorSupplierName ?? '' },
          {
            header: 'Adult cost',
            value: (r) => r.costPerAdult,
          },
          { header: 'Status', value: (r) => r.status },
        ],
      }}
      copy={{
        searchPlaceholder: 'Search by name, restaurant, city or vendor (min 3 characters)',
        emptyTitle: 'No meal services match these filters',
        emptyDescription: 'Adjust filters or clear the search box.',
        errorTitle: 'Failed to load meal services',
        errorDescription: 'The server returned an error. Try refreshing or check your network.',
      }}
    />
  );
};
